import React from 'react'
import { change, Field, getFormValues } from 'redux-form'
import { Select, TextInput } from 'SRC/ui/FormElements'
import { CitiesApi } from 'SRC/modules/geoObjects/cities/api'
import { LocationsApi } from 'SRC/modules/geoObjects/locations/api'
import { getCountries } from 'SRC/modules/geoObjects/countries/selectors'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import withTranslation from 'next-translate/withTranslation'
import { getUserInfo } from '../../../../../selectors'

export class Address extends React.Component {
  state = {
    isCitiesLoading: true,
    cities: [],
    isLocationsLoading: true,
    locations: []
  }
  isComponentMounted = true

  async componentDidMount () {
    if (this.props.currentAddress && this.props.currentAddress.id) {
      if (this.props.address.country && this.props.address.country.id) {
        const country = Number(this.props.address.country.id)
        await this.loadCitiesToState(country)
      }
      if (this.props.address.city && this.props.address.city.id) {
        const city = Number(this.props.address.city.id)
        this.saveRegion(city)
        await this.loadLocationToState(city)
      }
    }
  }

  componentWillUnmount () {
    this.isComponentMounted = false
  }

  getCountryField = () => {
    const { i18n: { t }, index } = this.props

    return {
      id: `country-${index}`,
      name: `userAddresses[${index}].country`,
      label: t('common:Država'),
      options: this.prepareCountries(),
      onChange: this.onCountryChange,
      isRequired: true,
      disabled: false
    }
  }

  getRegionField = () => ({
    id: `region-${this.props.index}`,
    name: `userAddresses[${this.props.index}].region`
  })

  getCityField = () => {
    const { i18n: { t } } = this.props

    return {
      id: `city-${this.props.index}`,
      name: `userAddresses[${this.props.index}].city`,
      label: t('common:Grad'),
      options: this.state.cities,
      disabled: this.state.isCitiesLoading,
      isRequired: true,
      onChange: this.onCityChange
    }
  }

  getLocationField = () => {
    const { i18n: { t } } = this.props

    return {
      id: `location-${this.props.index}`,
      name: `userAddresses[${this.props.index}].location`,
      label: t('common:Lokacija'),
      options: this.state.locations,
      isRequired: true,
      disabled: this.state.isLocationsLoading
    }
  }

  getStreetField = () => {
    const { i18n: { t } } = this.props

    return {
      id: `street-${this.props.index}`,
      name: `userAddresses[${this.props.index}].street`,
      label: t('common:Ulica i broj'),
      isRequired: Boolean(this.props.isBusiness)
    }
  }

  getZipField = () => {
    const { i18n: { t } } = this.props

    return {
      id: `zip-${this.props.index}`,
      name: `userAddresses[${this.props.index}].zip`,
      label: t('common:Poštanski broj'),
      formName: 'update'
    }
  }

  prepareCountries = () => this.props.countries.filter(country => !country.parent).map(country => {
    const isGrouped = Boolean(this.props.countries.filter(item => item.parent === country.id).length)
    return {
      id: country.id,
      label: country.label,
      isGrouped,
      isDisabled: false,
      value: country.id,
      options: isGrouped ? this.props.countries.filter(item => item.parent === country.id).map(item => ({
        value: item.id,
        isDisabled: false,
        label: item.label
      })) : null
    }
  })

  onCountryChange = async e => {
    const country = Number(e.target.value)
    await this.props.change('update', this.getRegionField().name, '')
    await this.props.change('update', this.getCityField().name, '')
    await this.props.change('update', this.getLocationField().name, '')
    await this.loadCitiesToState(country)
  }

  loadCitiesToState = async country => {
    if (this.isComponentMounted) {
      await this.setState({
        isCitiesLoading: true,
        isLocationsLoading: true,
        locations: []
      })
    }
    const citiesApi = new CitiesApi()
    const citiesWithinRegions = await citiesApi.getCitiesByCountryWithinRegions(country)
    const preparedCities = this.prepareCities(citiesWithinRegions)

    if (this.isComponentMounted) {
      await this.setState({
        cities: preparedCities,
        isCitiesLoading: false
      })
    }
  }

  prepareCities = (citiesWithinRegions = []) => citiesWithinRegions.map(region => ({
    id: region.id,
    label: region.label,
    value: `region-${region.id}`,
    isGrouped: true,
    options: region.cities.map(city => ({
      id: city.id,
      label: city.label,
      value: city.value,
      region: region.id
    }))
  }))

  onCityChange = async e => {
    const city = Number(e.target.value)
    this.saveRegion(city)
    await this.props.change('update', this.getLocationField().name, '')
    await this.loadLocationToState(city)
  }

  saveRegion = city => {
    const arCity = this.state.cities.filter(item => item.options.some(option => option.id === city))
    const selectedRegion = arCity.length ? `${arCity[0].id}` : null
    this.props.change('update', this.getRegionField().name, selectedRegion)
  }

  loadLocationToState = async city => {
    if (this.isComponentMounted) {
      await this.setState({...this.state, isLocationsLoading: true})
    }
    const api = new LocationsApi()
    const locations = await api.getLocationsByCity(city)
    if (this.isComponentMounted) {
      await this.setState({
        ...this.state,
        locations: this.prepareLocation(locations),
        isLocationsLoading: !locations.length
      })
    }
  }

  prepareLocation = (locations = []) => {
    const sortedLocations = locations.sort((a, b) => {
      if (a.priority === b.priority) return 0
      else if (a.priority === null) return 1
      else if (b.priority === null) return -1
      else return a.priority - b.priority
    })
    return sortedLocations.map(({id, label, value}) => ({id, label, value}))
  }

  render () {
    const country = this.getCountryField()
    const region = this.getRegionField()
    const city = this.getCityField()
    const location = this.getLocationField()
    const street = this.getStreetField()
    const zip = this.getZipField()
    return [
      <div className='form-drzava' key='countryAndCity'>
        <Select {...country} />
        <Field component='input' type='hidden' {...region} />
        <Select {...city} />
      </div>,
      <div className='form-lokacija' key='locationAndStreet'>
        <Select {...location} />
        <TextInput {...street} />
      </div>,
      <div className='form-postanski-broj' key='zip'>
        <TextInput {...zip} />
      </div>
    ]
  }
}

const mapStateToProps = (state, props) => {
  const result = {countries: getCountries(state), address: {}, currentAddresses: {}}
  const userInfo = getUserInfo(state)
  if (
    props.index >= 0 &&
    userInfo &&
    Array.isArray(userInfo.userAddresses) &&
    userInfo.userAddresses.length &&
    userInfo.userAddresses[props.index]) {
    result.address = {...userInfo.userAddresses[props.index]}
  }
  const values = getFormValues('update')(state)
  if (
    props.index >= 0 &&
    values &&
    Array.isArray(values.userAddresses) &&
    values.userAddresses.length &&
    values.userAddresses[props.index]
  ) {
    result.currentAddress = {...values.userAddresses[props.index]}
  }
  return result
}

Address.propTypes = {
  change: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    parent: PropTypes.number,
    label: PropTypes.string.isRequired
  })).isRequired,
  index: PropTypes.number.isRequired,
  address: PropTypes.shape({
    id: PropTypes.number,
    country: PropTypes.shape({
      id: PropTypes.number.isRequired
    }),
    region: PropTypes.shape({
      id: PropTypes.number.isRequired
    }),
    city: PropTypes.shape({
      id: PropTypes.number.isRequired
    }),
    location: PropTypes.shape({
      id: PropTypes.number.isRequired
    }),
    street: PropTypes.string,
    zip: PropTypes.string,
    monfriFrom: PropTypes.string,
    monfriTo: PropTypes.string,
    saturdayFrom: PropTypes.string,
    saturdayTo: PropTypes.string,
    sundayFrom: PropTypes.string,
    sundayTo: PropTypes.string
  }),
  currentAddress: PropTypes.shape({
    id: PropTypes.number
  }),
  isBusiness: PropTypes.bool.isRequired
}

Address.defaultProps = {
  countries: [],
  index: 0,
  isBusiness: false
}

const enhance = compose(
  connect(mapStateToProps, {change}),
  withTranslation
)

export default enhance(Address)
